import { FilterSpendingDemand, SpendingDemandProcessBy } from "../types/spendingDemand.types";
import { Filter } from "../../../utils/genericAPI/useGenericAPI";
import { OperatorQuery } from "../../../utils/genericAPI/queryURL.utils";

/**
 * Convert FilterSpendingDemand to Filter format expected by useGenericAPI
 */
export const convertFilterSpendingDemandToGenericFilter = (
  filter: FilterSpendingDemand | null | undefined
): Filter<Partial<SpendingDemandProcessBy>> | undefined => {
  if (!filter) {
    return undefined;
  }

  const result: Filter<Partial<SpendingDemandProcessBy>> = {};

  // Convert status filter
  if (filter.status && filter.status.length > 0) {
    if (filter.status.length === 1) {
      result.spending_demand_status = `=${filter.status[0]}`;
    } else {
      result.spending_demand_status = `<in>[${filter.status.join(',')}]` as OperatorQuery;
    }
  }

  // Convert type filter
  if (filter.type && filter.type.length > 0) {
    if (filter.type.length === 1) {
      result.spending_demand_type = `=${filter.type[0]}`;
    } else {
      result.spending_demand_type = `<in>[${filter.type.join(',')}]` as OperatorQuery;
    }
  }

  // Convert amountTTC filter
  if (filter.amountTTC?.min !== undefined || filter.amountTTC?.max !== undefined) {
    if (filter.amountTTC.min !== undefined && filter.amountTTC.max !== undefined) {
      result.spending_demand_amountTTC = `<<btw>>[${filter.amountTTC.min},${filter.amountTTC.max}]` as OperatorQuery;
    } else if (filter.amountTTC.min !== undefined) {
      result.spending_demand_amountTTC = `>>=${filter.amountTTC.min}` as OperatorQuery;
    } else if (filter.amountTTC.max !== undefined) {
      result.spending_demand_amountTTC = `<<=${filter.amountTTC.max}` as OperatorQuery;
    }
  }

  // Convert date filter
  if (filter.date?.min || filter.date?.max) {
    if (filter.date.min && filter.date.max) {
      result.spending_demand_date = `<<btw>>[${filter.date.min},${filter.date.max}]` as OperatorQuery;
    } else if (filter.date.min) {
      result.spending_demand_date = `>=${filter.date.min}` as OperatorQuery;
    } else if (filter.date.max) {
      result.spending_demand_date = `<=${filter.date.max}` as OperatorQuery;
    }
  }

  // Convert userId filter
  if (filter.userId && filter.userId.length > 0) {
    if (filter.userId.length === 1) {
      result.spending_demand_user = `=${filter.userId[0]}`;
    } else {
      result.spending_demand_user = `<in>[${filter.userId.join(',')}]` as OperatorQuery;
    }
  }

  // Convert projectId filter
  if (filter.projectId && filter.projectId.length > 0) {
    if (filter.projectId.length === 1) {
      result.spending_demand_project = `=${filter.projectId[0]}`;
    } else {
      result.spending_demand_project = `<in>[${filter.projectId.join(',')}]` as OperatorQuery;
    }
  }

  // Convert boxAssociationId filter
  if (filter.boxAssociationId && filter.boxAssociationId.length > 0) {
    if (filter.boxAssociationId.length === 1) {
      result.spending_demand_box_association_id = `=${filter.boxAssociationId[0]}`;
    } else {
      result.spending_demand_box_association_id = `<in>[${filter.boxAssociationId.join(',')}]` as OperatorQuery;
    }
  }

  // Convert readableId filter (prefix match via LIKE)
  if (filter.readableId) {
    result.spending_demand_readable_id = `<like>${filter.readableId}` as OperatorQuery;
  }

  // Return undefined if no filters are set
  if (Object.keys(result).length === 0) {
    return undefined;
  }

  return result;
};

/**
 * Convert Filter format from useGenericAPI back to FilterSpendingDemand
 */
export const convertGenericFilterToFilterSpendingDemand = (
  currentFilterObject: any,
  currentFilter: string | null | undefined
): FilterSpendingDemand | null => {
  // Try to parse from currentFilter string if available
  if (currentFilter) {
    const filterObj: FilterSpendingDemand = {};
    const conditions = currentFilter.split('|');
    
    conditions.forEach((condition: string) => {
      // Parse status
      if (condition.includes('spending_demand_status')) {
        if (condition.includes('<in>')) {
          const match = condition.match(/spending_demand_status<in>\[(.+)\]/);
          if (match) {
            filterObj.status = match[1].split(',');
          }
        } else if (condition.includes('=')) {
          const match = condition.match(/spending_demand_status=(.+)/);
          if (match) {
            filterObj.status = [match[1]];
          }
        }
      }
      // Parse type
      if (condition.includes('spending_demand_type')) {
        if (condition.includes('<in>')) {
          const match = condition.match(/spending_demand_type<in>\[(.+)\]/);
          if (match) {
            filterObj.type = match[1].split(',') as any;
          }
        } else if (condition.includes('=')) {
          const match = condition.match(/spending_demand_type=(.+)/);
          if (match) {
            filterObj.type = [match[1]] as any;
          }
        }
      }
      // Parse amountTTC
      if (condition.includes('spending_demand_amountTTC')) {
        if (condition.includes('<<btw>>')) {
          const match = condition.match(/spending_demand_amountTTC<<btw>>\[(.+),(.+)\]/);
          if (match) {
            filterObj.amountTTC = {
              min: parseFloat(match[1]),
              max: parseFloat(match[2])
            };
          }
        } else if (condition.includes('>>=')) {
          const match = condition.match(/spending_demand_amountTTC>>=(.+)/);
          if (match) {
            filterObj.amountTTC = { min: parseFloat(match[1]) };
          }
        } else if (condition.includes('<<=')) {
          const match = condition.match(/spending_demand_amountTTC<<=(.+)/);
          if (match) {
            filterObj.amountTTC = { max: parseFloat(match[1]) };
          }
        }
      }
      // Parse date
      if (condition.includes('spending_demand_date')) {
        if (condition.includes('<<btw>>')) {
          const match = condition.match(/spending_demand_date<<btw>>\[(.+),(.+)\]/);
          if (match) {
            filterObj.date = {
              min: match[1],
              max: match[2]
            };
          }
        } else if (condition.includes('>=')) {
          const match = condition.match(/spending_demand_date>=(.+)/);
          if (match) {
            filterObj.date = { min: match[1] };
          }
        } else if (condition.includes('<=')) {
          const match = condition.match(/spending_demand_date<=(.+)/);
          if (match) {
            filterObj.date = { max: match[1] };
          }
        }
      }
      // Parse userId
      if (condition.includes('spending_demand_user')) {
        if (condition.includes('<in>')) {
          const match = condition.match(/spending_demand_user<in>\[(.+)\]/);
          if (match) {
            filterObj.userId = match[1].split(',');
          }
        } else if (condition.includes('=')) {
          const match = condition.match(/spending_demand_user=(.+)/);
          if (match) {
            filterObj.userId = [match[1]];
          }
        }
      }
      // Parse projectId
      if (condition.includes('spending_demand_project')) {
        if (condition.includes('<in>')) {
          const match = condition.match(/spending_demand_project<in>\[(.+)\]/);
          if (match) {
            filterObj.projectId = match[1].split(',');
          }
        } else if (condition.includes('=')) {
          const match = condition.match(/spending_demand_project=(.+)/);
          if (match) {
            filterObj.projectId = [match[1]];
          }
        }
      }
      // Parse boxAssociationId
      if (condition.includes('spending_demand_box_association_id')) {
        if (condition.includes('<in>')) {
          const match = condition.match(/spending_demand_box_association_id<in>\[(.+)\]/);
          if (match) {
            filterObj.boxAssociationId = match[1].split(',');
          }
        } else if (condition.includes('=')) {
          const match = condition.match(/spending_demand_box_association_id=(.+)/);
          if (match) {
            filterObj.boxAssociationId = [match[1]];
          }
        }
      }
      // Parse readableId
      if (condition.includes('spending_demand_readable_id')) {
        const likeMatch = condition.match(/spending_demand_readable_id<like>(.+)/);
        const eqMatch = condition.match(/spending_demand_readable_id=(.+)/);
        const match = likeMatch || eqMatch;
        if (match) {
          filterObj.readableId = match[1];
        }
      }
    });
    
    return Object.keys(filterObj).length > 0 ? filterObj : null;
  }
  
  return null;
};

